import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  getFilteredSessions,
  getAnalyticsData,
  getTimeSeriesAnalytics,
  getTrendsAnalytics,
  getDetailedAnalytics,
  getUsersOrderedBySessions,
  getDistinctBrandIds,
  getAvailableModels,
  updateMessageMetadata,
  AdminSessionV2FilterPayload,
  AdminFilteredSessionsV2Response,
  AdminAnalyticsV2Response,
  DistinctBrandIdsV2Response,
  FeedbackTypeV2,
} from "../api";

// New analytics types for enhanced endpoints
export interface TimeSeriesAnalytics {
  time_range: string;
  granularity: string;
  data_points: Array<{
    timestamp: Date;
    session_count: number;
    message_count: number;
    liked_count: number;
    disliked_count: number;
    no_feedback_count: number;
    unique_users: number;
    avg_session_length: number;
  }>;
  total_sessions: number;
  total_messages: number;
  overall_satisfaction_rate: number;
}

export interface TrendsAnalytics {
  sessions_trend: {
    period: string;
    current_value: number;
    previous_value: number;
    change_percentage: number;
    change_direction: string;
  };
  satisfaction_trend: {
    period: string;
    current_value: number;
    previous_value: number;
    change_percentage: number;
    change_direction: string;
  };
  user_engagement_trend: {
    period: string;
    current_value: number;
    previous_value: number;
    change_percentage: number;
    change_direction: string;
  };
  message_volume_trend: {
    period: string;
    current_value: number;
    previous_value: number;
    change_percentage: number;
    change_direction: string;
  };
}

export interface DetailedAnalytics {
  overview: {
    total_sessions: number;
    total_users: number;
    sessions_with_feedback: number;
    avg_session_length: number;
    total_messages: number;
  };
  model_performance: Array<{
    model_name: string;
    session_count: number;
    message_count: number;
    satisfaction_rate: number;
    avg_response_time?: number;
    most_common_topics: string[];
  }>;
  brand_analytics: Array<{
    brand_id: string;
    session_count: number;
    user_count: number;
    satisfaction_rate: number;
    avg_session_length: number;
    top_models_used: string[];
  }>;
  user_engagement: {
    time_period: string;
    active_users: number;
    returning_users: number;
    new_users: number;
    avg_sessions_per_user: number;
    avg_messages_per_session: number;
  };
  topic_analytics: Array<{
    topic: string;
    frequency: number;
    satisfaction_rate: number;
    common_keywords: string[];
  }>;
  peak_usage_times: Array<{
    hour: number;
    session_count: number;
  }>;
}

export interface UseAdminDataProps {
  autoFetch?: boolean;
  initialFilters?: Partial<AdminSessionV2FilterPayload>;
}

export interface AdminDataState {
  sessions: AdminFilteredSessionsV2Response | null;
  analytics: AdminAnalyticsV2Response | null;
  timeSeriesAnalytics: TimeSeriesAnalytics | null;
  trendsAnalytics: TrendsAnalytics | null;
  detailedAnalytics: DetailedAnalytics | null;
  users: string[];
  brands: DistinctBrandIdsV2Response | null;
  models: string[];
  loading: {
    sessions: boolean;
    analytics: boolean;
    timeSeriesAnalytics: boolean;
    trendsAnalytics: boolean;
    detailedAnalytics: boolean;
    users: boolean;
    brands: boolean;
    models: boolean;
  };
  errors: {
    sessions: string | null;
    analytics: string | null;
    timeSeriesAnalytics: string | null;
    trendsAnalytics: string | null;
    detailedAnalytics: string | null;
    users: string | null;
    brands: string | null;
    models: string | null;
  };
}

// Move default parameters outside the hook to prevent recreation
const DEFAULT_TIME_SERIES_PARAMS = {
  time_range: "last_30d",
  granularity: "day",
} as const;

const DEFAULT_TRENDS_PERIOD = "last_30d" as const;

const DEFAULT_DETAILED_PARAMS = { 
  time_range: "last_30d" 
} as const;

export const useAdminData = ({
  autoFetch = true,
  initialFilters = {},
}: UseAdminDataProps = {}) => {
  // Create stable initial filters to prevent unnecessary re-renders
  const stableInitialFilters = useMemo(
    () => ({
      page_size: 10000, // Large number to fetch all sessions for client-side pagination
      feedback_type: FeedbackTypeV2.ALL,
      // Note: exclude_workflow_tests is handled client-side in SessionsPanel
      ...initialFilters,
    }),
    [initialFilters]
  );

  const [state, setState] = useState<AdminDataState>({
    sessions: null,
    analytics: null,
    timeSeriesAnalytics: null,
    trendsAnalytics: null,
    detailedAnalytics: null,
    users: [],
    brands: null,
    models: [],
    loading: {
      sessions: false,
      analytics: false,
      timeSeriesAnalytics: false,
      trendsAnalytics: false,
      detailedAnalytics: false,
      users: false,
      brands: false,
      models: false,
    },
    errors: {
      sessions: null,
      analytics: null,
      timeSeriesAnalytics: null,
      trendsAnalytics: null,
      detailedAnalytics: null,
      users: null,
      brands: null,
      models: null,
    },
  });

  const [filters, setFilters] =
    useState<AdminSessionV2FilterPayload>(stableInitialFilters);

  // Helper to update loading state
  const setLoading = useCallback(
    (key: keyof AdminDataState["loading"], value: boolean) => {
      setState((prev) => ({
        ...prev,
        loading: { ...prev.loading, [key]: value },
      }));
    },
    []
  );

  // Helper to set error state
  const setError = useCallback(
    (key: keyof AdminDataState["errors"], value: string | null) => {
      setState((prev) => ({
        ...prev,
        errors: { ...prev.errors, [key]: value },
      }));
    },
    []
  );

  // Fetch sessions with filters
  const fetchSessions = useCallback(
    async (customFilters?: Partial<AdminSessionV2FilterPayload>) => {
      const filtersToUse = customFilters || filters;
      console.log('[useAdminData] fetchSessions called with filters:', filtersToUse);
      setLoading("sessions", true);
      setError("sessions", null);

      try {
        console.log('[useAdminData] About to call getFilteredSessions with:', filtersToUse);
        const data = await getFilteredSessions(filtersToUse);
        console.log('[useAdminData] getFilteredSessions returned:', data);
        setState((prev) => ({ ...prev, sessions: data }));
        return data;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to fetch sessions";
        setError("sessions", errorMessage);
        console.error("Error fetching sessions:", error);
        throw error;
      } finally {
        setLoading("sessions", false);
      }
    },
    [filters, setLoading, setError]
  );

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    setLoading("analytics", true);
    setError("analytics", null);

    try {
      const data = await getAnalyticsData();
      setState((prev) => ({ ...prev, analytics: data }));
      return data;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch analytics";
      setError("analytics", errorMessage);
      console.error("Error fetching analytics:", error);
      throw error;
    } finally {
      setLoading("analytics", false);
    }
  }, [setLoading, setError]);

  // Fetch time series analytics - optimized with stable params
  const fetchTimeSeriesAnalytics = useCallback(
    async (params: Record<string, any> = DEFAULT_TIME_SERIES_PARAMS) => {
      setLoading("timeSeriesAnalytics", true);
      setError("timeSeriesAnalytics", null);
      try {
        const data = await getTimeSeriesAnalytics(params);
        setState((prev) => ({ ...prev, timeSeriesAnalytics: data }));
        return data;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to fetch time series analytics";
        setError("timeSeriesAnalytics", errorMessage);
        console.error("Error fetching time series analytics:", error);
        throw error;
      } finally {
        setLoading("timeSeriesAnalytics", false);
      }
    },
    [setLoading, setError]
  );

  // Fetch trends analytics - optimized with stable params
  const fetchTrendsAnalytics = useCallback(
    async (compare_period: string = DEFAULT_TRENDS_PERIOD) => {
      setLoading("trendsAnalytics", true);
      setError("trendsAnalytics", null);
      try {
        const data = await getTrendsAnalytics(compare_period);
        setState((prev) => ({ ...prev, trendsAnalytics: data }));
        return data;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to fetch trends analytics";
        setError("trendsAnalytics", errorMessage);
        console.error("Error fetching trends analytics:", error);
        throw error;
      } finally {
        setLoading("trendsAnalytics", false);
      }
    },
    [setLoading, setError]
  );

  // Fetch detailed analytics - optimized with stable params
  const fetchDetailedAnalytics = useCallback(
    async (params: Record<string, any> = DEFAULT_DETAILED_PARAMS) => {
      setLoading("detailedAnalytics", true);
      setError("detailedAnalytics", null);
      try {
        const data = await getDetailedAnalytics(params);
        setState((prev) => ({ ...prev, detailedAnalytics: data }));
        return data;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to fetch detailed analytics";
        setError("detailedAnalytics", errorMessage);
        console.error("Error fetching detailed analytics:", error);
        throw error;
      } finally {
        setLoading("detailedAnalytics", false);
      }
    },
    [setLoading, setError]
  );

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading("users", true);
    setError("users", null);

    try {
      const data = await getUsersOrderedBySessions();
      setState((prev) => ({ ...prev, users: data }));
      return data;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch users";
      setError("users", errorMessage);
      console.error("Error fetching users:", error);
      throw error;
    } finally {
      setLoading("users", false);
    }
  }, [setLoading, setError]);

  // Fetch brands
  const fetchBrands = useCallback(async () => {
    setLoading("brands", true);
    setError("brands", null);

    try {
      const data = await getDistinctBrandIds();
      setState((prev) => ({ ...prev, brands: data }));
      return data;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch brands";
      setError("brands", errorMessage);
      console.error("Error fetching brands:", error);
      throw error;
    } finally {
      setLoading("brands", false);
    }
  }, [setLoading, setError]);

  // Fetch models
  const fetchModels = useCallback(async () => {
    setLoading("models", true);
    setError("models", null);

    try {
      const data = await getAvailableModels();
      setState((prev) => ({ ...prev, models: data }));
      return data;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch models";
      setError("models", errorMessage);
      console.error("Error fetching models:", error);
      throw error;
    } finally {
      setLoading("models", false);
    }
  }, [setLoading, setError]);

  // Update message metadata
  const updateMessageMeta = useCallback(
    async (messageRunId: string, key: string, value: any) => {
      try {
        const result = await updateMessageMetadata(messageRunId, key, value);
        // Optionally refresh sessions data to reflect changes
        await fetchSessions();
        return result;
      } catch (error) {
        console.error("Error updating message metadata:", error);
        throw error;
      }
    },
    [fetchSessions]
  );

  // Update filters - now with proper change detection
  const updateFilters = useCallback(
    (newFilters: Partial<AdminSessionV2FilterPayload>) => {
      console.log('[useAdminData] updateFilters called with:', newFilters);
      setFilters((prev) => {
        const merged = { ...prev, ...newFilters };
        console.log('[useAdminData] Previous filters:', prev);
        console.log('[useAdminData] Merged filters:', merged);
        
        // Only reset to page 1 when changing filters OTHER than page/page_size
        // and when page is not explicitly being set in newFilters
        const hasNonPageChanges = Object.keys(newFilters).some((key) => key !== "page" && key !== "page_size");
        const isExplicitPageChange = newFilters.hasOwnProperty('page');
        
        if (hasNonPageChanges && !isExplicitPageChange) {
          merged.page = 1;
          console.log('[useAdminData] Reset page to 1 due to non-page filter changes');
        }
        
        console.log('[useAdminData] Final merged filters:', merged);
        return merged;
      });
    },
    []
  );

  // Refresh all data - optimized to use stable parameters
  const refreshAll = useCallback(
    async (customFilters?: Partial<AdminSessionV2FilterPayload>) => {
      const promises = [
        fetchSessions(customFilters), // ensure we use the latest provided filters
        fetchAnalytics(),
        fetchUsers(),
        fetchBrands(),
        fetchModels(),
        fetchTimeSeriesAnalytics(DEFAULT_TIME_SERIES_PARAMS),
        fetchTrendsAnalytics(DEFAULT_TRENDS_PERIOD),
        fetchDetailedAnalytics(DEFAULT_DETAILED_PARAMS),
      ];

      try {
        await Promise.allSettled(promises);
      } catch (error) {
        console.error("Error refreshing data:", error);
      }
    },
    [
      fetchSessions,
      fetchAnalytics,
      fetchUsers,
      fetchBrands,
      fetchModels,
      fetchTimeSeriesAnalytics,
      fetchTrendsAnalytics,
      fetchDetailedAnalytics,
    ]
  );

  // Clear all errors
  const clearErrors = useCallback(() => {
    setState((prev) => ({
      ...prev,
      errors: {
        sessions: null,
        analytics: null,
        timeSeriesAnalytics: null,
        trendsAnalytics: null,
        detailedAnalytics: null,
        users: null,
        brands: null,
        models: null,
      },
    }));
  }, []);

  // Auto-fetch effect for sessions when filters change
  useEffect(() => {
    console.log('[useAdminData] Filter change effect triggered');
    console.log('[useAdminData] Current filters:', filters);
    console.log('[useAdminData] autoFetch:', autoFetch);
    
    if (autoFetch) {
      console.log('[useAdminData] Calling fetchSessions due to filter change');
      fetchSessions().then(() => {
        console.log('[useAdminData] fetchSessions completed successfully');
      }).catch((error) => {
        console.error('[useAdminData] fetchSessions failed:', error);
      });
    }
  }, [
    filters.page, 
    filters.page_size, 
    filters.search_term,
    filters.tag_search,
    filters.feedback_type,
    filters.user_id,
    filters.brand_id,
    filters.model,
    filters.start_date,
    filters.end_date,
    filters.min_chat_length,
    filters.max_chat_length,
    autoFetch,
    fetchSessions
  ]); // Watch specific filter properties instead of the entire object

  // Auto-fetch extended analytics data once on mount when autoFetch is true
  // Removed unstable dependencies to prevent unnecessary calls
  useEffect(() => {
    if (autoFetch) {
      fetchTimeSeriesAnalytics(DEFAULT_TIME_SERIES_PARAMS);
      fetchTrendsAnalytics(DEFAULT_TRENDS_PERIOD);
      fetchDetailedAnalytics(DEFAULT_DETAILED_PARAMS);
    }
  }, [autoFetch]); // Removed function dependencies

  // Initial data load effect - optimized dependencies
  useEffect(() => {
    if (autoFetch) {
      Promise.allSettled([
        fetchAnalytics(),
        fetchUsers(),
        fetchBrands(),
        fetchModels(),
      ]).catch(console.error);
    }
  }, [autoFetch]); // Removed function dependencies

  // Computed values
  const isLoading = Object.values(state.loading).some(Boolean);
  const hasError = Object.values(state.errors).some(Boolean);

  return {
    // Data
    sessions: state.sessions,
    analytics: state.analytics,
    timeSeriesAnalytics: state.timeSeriesAnalytics,
    trendsAnalytics: state.trendsAnalytics,
    detailedAnalytics: state.detailedAnalytics,
    users: state.users,
    brands: state.brands,
    models: state.models,

    // Loading states
    loading: state.loading,
    isLoading,

    // Error states
    errors: state.errors,
    hasError,

    // Current filters
    filters,

    // Actions
    fetchSessions,
    fetchAnalytics,
    fetchTimeSeriesAnalytics,
    fetchTrendsAnalytics,
    fetchDetailedAnalytics,
    fetchUsers,
    fetchBrands,
    fetchModels,
    updateMessageMeta,
    updateFilters,
    refreshAll,
    clearErrors,
  };
};
